import React, { useMemo } from 'react';
import {
  TrendingUp, Building2, Users, Pill, UserCheck,
  CheckCircle2, Clock, XCircle, BarChart2, PieChart,
  ArrowUpRight, Activity, AlertTriangle
} from 'lucide-react';

// ─── tiny helpers ────────────────────────────────────────────────────────────
const pct = (n, total) => (total === 0 ? 0 : Math.round((n / total) * 100));

// ─── DonutRing ────────────────────────────────────────────────────────────────
const DonutRing = ({ active, inactive, color = '#0d9488', size = 80 }) => {
  const total = active + inactive;
  const r = 28;
  const circ = 2 * Math.PI * r;
  const activeDash = total === 0 ? 0 : (active / total) * circ;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64">
      <circle cx="32" cy="32" r={r} fill="none" stroke="#e2e8f0" strokeWidth="8" />
      {total > 0 && (
        <circle
          cx="32" cy="32" r={r}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={`${activeDash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 32 32)"
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
      )}
      <text x="32" y="36" textAnchor="middle" fontSize="11" fontWeight="800" fill="#0f172a">
        {total === 0 ? '0' : `${pct(active, total)}%`}
      </text>
    </svg>
  );
};

// ─── HBar ─────────────────────────────────────────────────────────────────────
const HBar = ({ label, value, max, color }) => {
  const width = max === 0 ? 0 : Math.round((value / max) * 100);
  return (
    <div style={{ marginBottom: '0.65rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '0.78rem', color: '#475569', fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: '0.78rem', color: '#0f172a', fontWeight: 800 }}>{value}</span>
      </div>
      <div style={{ height: 8, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
        <div
          style={{
            height: '100%',
            width: `${width}%`,
            background: color,
            borderRadius: 999,
            transition: 'width 0.6s ease',
          }}
        />
      </div>
    </div>
  );
};

// ─── StackedBar ───────────────────────────────────────────────────────────────
const StackedBar = ({ label, active, inactive, deactivated }) => {
  const total = active + inactive + deactivated;
  const aPct = pct(active, total);
  const iPct = pct(inactive, total);
  const dPct = pct(deactivated, total);
  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#0f172a' }}>{label}</span>
        <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 600 }}>{total} total</span>
      </div>
      <div style={{ display: 'flex', height: 12, borderRadius: 999, overflow: 'hidden', background: '#f1f5f9' }}>
        {aPct > 0 && <div style={{ width: `${aPct}%`, background: '#10b981', transition: 'width 0.5s' }} title={`Active: ${active}`} />}
        {iPct > 0 && <div style={{ width: `${iPct}%`, background: '#f59e0b', transition: 'width 0.5s' }} title={`Pending: ${inactive}`} />}
        {dPct > 0 && <div style={{ width: `${dPct}%`, background: '#ef4444', transition: 'width 0.5s' }} title={`Deactivated: ${deactivated}`} />}
      </div>
      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.35rem' }}>
        <span style={{ fontSize: '0.68rem', color: '#10b981', fontWeight: 700 }}>● {active} Active</span>
        <span style={{ fontSize: '0.68rem', color: '#f59e0b', fontWeight: 700 }}>● {inactive} Pending</span>
        <span style={{ fontSize: '0.68rem', color: '#ef4444', fontWeight: 700 }}>● {deactivated} Inactive</span>
      </div>
    </div>
  );
};

// ─── Main Analytics component ─────────────────────────────────────────────────
const Analytics = ({ clinics, doctors, pharmacies, supervisors }) => {
  const stats = useMemo(() => {
    const calc = (arr, statusField = 'vendor_approve_status') => ({
      total: arr.length,
      active: arr.filter(x => x[statusField] === 'A').length,
      pending: arr.filter(x => x[statusField] !== 'A' && x[statusField] !== 'D').length,
      deactivated: arr.filter(x => x[statusField] === 'D').length,
    });
    const c = calc(clinics);
    const d = calc(doctors);
    const p = calc(pharmacies);
    const s = calc(supervisors);
    const allTotal = c.total + d.total + p.total + s.total;
    const allActive = c.active + d.active + p.active + s.active;
    const allPending = c.pending + d.pending + p.pending + s.pending;
    const allDeactivated = c.deactivated + d.deactivated + p.deactivated + s.deactivated;
    const platformHealth = allTotal === 0 ? 0 : Math.round((allActive / allTotal) * 100);
    return { c, d, p, s, allTotal, allActive, allPending, allDeactivated, platformHealth };
  }, [clinics, doctors, pharmacies, supervisors]);

  // Specialty distribution
  const specialtyDist = useMemo(() => {
    const map = {};
    doctors.forEach(doc => {
      const key = doc.specialty || 'Unknown';
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
  }, [doctors]);

  // Clinic-to-doctor ratio
  const clinicDoctorRatio = useMemo(() => {
    if (!clinics.length) return [];
    const map = {};
    clinics.forEach(c => { map[c.id] = { name: c.vendor_name || 'Unnamed', doctors: 0, pharmacies: 0 }; });
    doctors.forEach(d => { if (map[d.clinic_id]) map[d.clinic_id].doctors++; });
    pharmacies.forEach(p => { if (map[p.clinic_id]) map[p.clinic_id].pharmacies++; });
    return Object.values(map)
      .filter(c => c.doctors > 0 || c.pharmacies > 0)
      .sort((a, b) => b.doctors - a.doctors)
      .slice(0, 6);
  }, [clinics, doctors, pharmacies]);

  const maxDoctors = Math.max(...clinicDoctorRatio.map(c => c.doctors), 1);

  const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: '1rem', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' };
  const heading = { fontSize: '0.9rem', fontWeight: 800, color: '#0f172a', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' };

  return (
    <div className="content-section">
      {/* Page header */}
      <div className="content-header" style={{ marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.2rem' }}>
            Analytics &amp; Reports
          </h1>
          <p style={{ fontSize: '0.8rem', color: '#64748b' }}>
            Platform-wide insights across all registered entities
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: stats.platformHealth >= 80 ? '#f0fdf4' : '#fffbeb', border: `1px solid ${stats.platformHealth >= 80 ? '#bbf7d0' : '#fde68a'}`, borderRadius: '0.75rem', padding: '0.5rem 0.9rem' }}>
          <Activity size={14} style={{ color: stats.platformHealth >= 80 ? '#16a34a' : '#d97706' }} />
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: stats.platformHealth >= 80 ? '#16a34a' : '#d97706' }}>
            Platform Health: {stats.platformHealth}%
          </span>
        </div>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Total Entities', value: stats.allTotal, icon: BarChart2, color: '#6366f1', bg: '#eef2ff' },
          { label: 'Active', value: stats.allActive, icon: CheckCircle2, color: '#10b981', bg: '#f0fdf4' },
          { label: 'Pending', value: stats.allPending, icon: Clock, color: '#f59e0b', bg: '#fffbeb' },
          { label: 'Deactivated', value: stats.allDeactivated, icon: XCircle, color: '#ef4444', bg: '#fef2f2' },
        ].map(kpi => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.label} style={{ ...card, display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div style={{ width: 44, height: 44, borderRadius: '0.75rem', background: kpi.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={20} style={{ color: kpi.color }} />
              </div>
              <div>
                <div style={{ fontSize: '1.6rem', fontWeight: 900, color: '#0f172a', lineHeight: 1 }}>{kpi.value}</div>
                <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, marginTop: '0.15rem' }}>{kpi.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Row 2: Donut cards + Stacked bars */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>

        {/* Entity active rate */}
        <div style={card}>
          <div style={heading}>
            <PieChart size={16} style={{ color: '#0d9488' }} />
            Active Rate by Entity
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem' }}>
            {[
              { label: 'Clinics', data: stats.c, color: '#0d9488', icon: Building2 },
              { label: 'Doctors', data: stats.d, color: '#6366f1', icon: Users },
              { label: 'Pharmacies', data: stats.p, color: '#10b981', icon: Pill },
              { label: 'Supervisors', data: stats.s, color: '#8b5cf6', icon: UserCheck },
            ].map(({ label, data, color, icon: Icon }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <DonutRing active={data.active} inactive={data.total - data.active} color={color} size={72} />
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginBottom: '0.2rem' }}>
                    <Icon size={13} style={{ color }} />
                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#0f172a' }}>{label}</span>
                  </div>
                  <div style={{ fontSize: '0.68rem', color: '#10b981', fontWeight: 700 }}>{data.active} active</div>
                  {data.pending > 0 && <div style={{ fontSize: '0.68rem', color: '#f59e0b', fontWeight: 700 }}>{data.pending} pending</div>}
                  {data.deactivated > 0 && <div style={{ fontSize: '0.68rem', color: '#ef4444', fontWeight: 700 }}>{data.deactivated} inactive</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Status breakdown stacked bars */}
        <div style={card}>
          <div style={heading}>
            <TrendingUp size={16} style={{ color: '#0d9488' }} />
            Status Breakdown
          </div>
          <StackedBar label="Clinics" active={stats.c.active} inactive={stats.c.pending} deactivated={stats.c.deactivated} />
          <StackedBar label="Doctors" active={stats.d.active} inactive={stats.d.pending} deactivated={stats.d.deactivated} />
          <StackedBar label="Pharmacies" active={stats.p.active} inactive={stats.p.pending} deactivated={stats.p.deactivated} />
          <StackedBar label="Supervisors" active={stats.s.active} inactive={stats.s.pending} deactivated={stats.s.deactivated} />
        </div>
      </div>

      {/* Row 3: Specialty dist + Clinic linkage */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>

        {/* Specialty distribution */}
        <div style={card}>
          <div style={heading}>
            <Users size={16} style={{ color: '#6366f1' }} />
            Top Doctor Specialties
          </div>
          {specialtyDist.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem', padding: '1.5rem 0' }}>No doctor data yet</div>
          ) : (
            specialtyDist.map(([spec, count]) => (
              <HBar key={spec} label={spec} value={count} max={specialtyDist[0][1]} color="#6366f1" />
            ))
          )}
        </div>

        {/* Doctors per clinic */}
        <div style={card}>
          <div style={heading}>
            <Building2 size={16} style={{ color: '#0d9488' }} />
            Doctors per Clinic
          </div>
          {clinicDoctorRatio.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem', padding: '1.5rem 0' }}>No linked data yet</div>
          ) : (
            clinicDoctorRatio.map(c => (
              <div key={c.name} style={{ marginBottom: '0.85rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#475569', maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
                  <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
                    <span style={{ color: '#6366f1', fontWeight: 700 }}>{c.doctors}d</span>
                    {' · '}
                    <span style={{ color: '#10b981', fontWeight: 700 }}>{c.pharmacies}ph</span>
                  </span>
                </div>
                <div style={{ height: 8, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct(c.doctors, maxDoctors)}%`, background: 'linear-gradient(90deg,#6366f1,#8b5cf6)', borderRadius: 999, transition: 'width 0.5s' }} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Summary table */}
      <div style={card}>
        <div style={heading}>
          <ArrowUpRight size={16} style={{ color: '#0d9488' }} />
          Platform Summary Report
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                {['Entity', 'Total', 'Active', 'Pending', 'Deactivated', 'Active Rate'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '0.6rem 1rem', fontSize: '0.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { name: 'Clinics', icon: Building2, color: '#0d9488', ...stats.c },
                { name: 'Doctors', icon: Users, color: '#6366f1', ...stats.d },
                { name: 'Pharmacies', icon: Pill, color: '#10b981', ...stats.p },
                { name: 'Supervisors', icon: UserCheck, color: '#8b5cf6', ...stats.s },
              ].map((row, i) => {
                const Icon = row.icon;
                const rate = pct(row.active, row.total);
                return (
                  <tr key={row.name} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '0.8rem 1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ width: 28, height: 28, borderRadius: '0.4rem', background: `${row.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon size={14} style={{ color: row.color }} />
                        </div>
                        <span style={{ fontWeight: 700, color: '#0f172a' }}>{row.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '0.8rem 1rem', fontWeight: 800, color: '#0f172a' }}>{row.total}</td>
                    <td style={{ padding: '0.8rem 1rem' }}>
                      <span style={{ background: '#f0fdf4', color: '#16a34a', fontWeight: 700, borderRadius: 999, padding: '0.2rem 0.6rem', fontSize: '0.72rem' }}>{row.active}</span>
                    </td>
                    <td style={{ padding: '0.8rem 1rem' }}>
                      <span style={{ background: '#fffbeb', color: '#d97706', fontWeight: 700, borderRadius: 999, padding: '0.2rem 0.6rem', fontSize: '0.72rem' }}>{row.pending}</span>
                    </td>
                    <td style={{ padding: '0.8rem 1rem' }}>
                      <span style={{ background: '#fef2f2', color: '#dc2626', fontWeight: 700, borderRadius: 999, padding: '0.2rem 0.6rem', fontSize: '0.72rem' }}>{row.deactivated}</span>
                    </td>
                    <td style={{ padding: '0.8rem 1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 999, overflow: 'hidden', minWidth: 60 }}>
                          <div style={{ height: '100%', width: `${rate}%`, background: rate >= 75 ? '#10b981' : rate >= 50 ? '#f59e0b' : '#ef4444', borderRadius: 999 }} />
                        </div>
                        <span style={{ fontSize: '0.72rem', fontWeight: 800, color: rate >= 75 ? '#16a34a' : rate >= 50 ? '#d97706' : '#dc2626', minWidth: 30 }}>{rate}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {/* Totals row */}
              <tr style={{ borderTop: '2px solid #e2e8f0', background: '#f8fafc' }}>
                <td style={{ padding: '0.8rem 1rem', fontWeight: 800, color: '#0f172a', fontSize: '0.85rem' }}>Platform Total</td>
                <td style={{ padding: '0.8rem 1rem', fontWeight: 900, color: '#0f172a', fontSize: '0.9rem' }}>{stats.allTotal}</td>
                <td style={{ padding: '0.8rem 1rem', fontWeight: 800, color: '#16a34a' }}>{stats.allActive}</td>
                <td style={{ padding: '0.8rem 1rem', fontWeight: 800, color: '#d97706' }}>{stats.allPending}</td>
                <td style={{ padding: '0.8rem 1rem', fontWeight: 800, color: '#dc2626' }}>{stats.allDeactivated}</td>
                <td style={{ padding: '0.8rem 1rem', fontWeight: 900, color: '#0f172a', fontSize: '0.85rem' }}>{stats.platformHealth}%</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Alert if deactivated > 0 */}
        {stats.allDeactivated > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginTop: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.65rem', padding: '0.65rem 1rem' }}>
            <AlertTriangle size={15} style={{ color: '#dc2626', flexShrink: 0 }} />
            <span style={{ fontSize: '0.78rem', color: '#b91c1c', fontWeight: 600 }}>
              {stats.allDeactivated} entit{stats.allDeactivated === 1 ? 'y is' : 'ies are'} currently deactivated and will not appear in patient-facing apps.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
